import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';

import { IPlotRange } from '../../models/range';
import { TimeRangeService } from '../../services/time-range';
import { RangeSelectorDialogComponent } from '../range-selector-dialog/range-selector-dialog.component';

/** Component for setting filters on the plot data */
@Component({
    selector: 'sci-range-buttons',
    templateUrl: './range-buttons.component.html',
    styleUrls: [ './range-buttons.component.scss' ]
})
export class RangeButtonsComponent implements OnInit {
    @Input() plotId: string;
    range: IPlotRange;
    hasHistory = false;

    constructor( private _dialog: MatDialog, private _timeRangeService: TimeRangeService ) {}

    ngOnInit() {
        this._timeRangeService.getPlotTimeRange$( this.plotId ).subscribe( newRange => {
            this.range = newRange;
            this.hasHistory = this._timeRangeService.getPlotHistoryLength( this.plotId ) > 0;
        });
    }

    /**
     * Sets a new total and/or visible range for the plot. If needed, the plot will download new data.
     * Adds the old range state to the history stack by default.
     *
     * @param newRange The new total and visible range the plot should display. If any of the values in newRange are undefined,
     *                 the plot's respective values will remain the same.
     * @param addToHistory Whether to add the plot's current range state to the history stack.
     */
    setRange( newRange: IPlotRange ) {
        newRange.isDate = true;
        this._timeRangeService.setPlotTimeRange( this.plotId, newRange );
    }

    /** Undoes the last range change performed on the plot */
    undoZoom() {
        this._timeRangeService.undoPlotTimeRangeChange( this.plotId );
    }

    /**
     * Sets a new range (both total and visible ranges) based on a duration,
     * with the center point of the currently visible range at the center of the new range.
     * @param duration The duration of the new range, in milliseconds.
     */
    setRangeByDuration( duration: number ) {
        this._timeRangeService.setRangeByDuration( this.plotId, duration );
    }

    /** Zooms the plot by an amount defined by the constant `ZOOM_OUT_RATIO` */
    zoomOut() {
        this._timeRangeService.zoomOut( this.plotId );
    }

    /** Zooms the plot by an amount defined by the constant `ZOOM_IN_RATIO` */
    zoomIn() {
        this._timeRangeService.zoomIn( this.plotId );
    }

    /** Pans the plot by an amount defined by the constant `PAN_LEFT_RATIO` */
    panLeft() {
        this._timeRangeService.panLeft( this.plotId );
    }

    /** Pans the plot by an amount defined by the constant `PAN_RIGHT_RATIO` */
    panRight() {
        this._timeRangeService.panRight( this.plotId );
    }

    openRangeSelectorDialog() {
        const dialogRef = this._dialog.open( RangeSelectorDialogComponent, {
            data: this.range
        });
        dialogRef.afterClosed().subscribe( newRange => {
            this.setRange( newRange );
        });
    }
}
